import React, { useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { type CustomThemeButtonProps, CustomThemeButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import PremiumIcon from '@atlaskit/icon/core/premium';
import { Box, xcss } from '@atlaskit/primitives';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { useJswPremiumTrialUpgradeLink } from '@atlassian/jira-external-urls/src/utils/jsw-premium-trial-upgrade-link/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { PremiumUpgradeModalEntryPointButton } from '@atlassian/jira-premium-upgrade-modal/src/ui/premium-upgrade-modal-entry-point-button/index.tsx';
import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { getTrialSprintBoardProjectId } from '@atlassian/jira-plans-banners/src/controllers/index.tsx';
import { getNumProjects } from '@atlassian/jira-plans-banners/src/controllers/getNumProjects.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME } from '../../../../common/constants.tsx';
import { FeatureGateLogo } from '../../../../common/ui/plan-feature-gate/index.tsx';
import messages from './messages.tsx';

const LEARN_MORE_LINK =
	'https://www.atlassian.com/software/jira/guides/advanced-roadmaps/overview#what-are-advanced-roadmaps';

// Statsig experiment `plans_menu_try_it_free_one_stop_shop_modal`
// the control cohort will be shown the modal, whereas the test cohort will be redirected to the billing page instead.
// if the check errors for whatever reason, we still want to show the modal, hence why it defaults to true
const PremiumFeatureGate = (): React.JSX.Element => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const {
		data: { premiumTrialUpgradeLink },
	} = expVal('plans_menu_try_it_free_one_stop_shop_modal', 'isOneStopShopModalEnabled', true)
		? { data: { premiumTrialUpgradeLink: undefined } }
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useJswPremiumTrialUpgradeLink({
				caller: PACKAGE_NAME,
			});

	const onStartTrialClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'tryItFree',
		});
	};

	const onLearnMoreClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'readTheGuide',
		});
	};

	// We expect all tenants to be "control" or "not-enrolled" if they see this modal, we are firing exposure (again) and including cohort in analytics for safety
	const stwpCohort = useMemo(() => {
		return fg('supercharge_timelines_with_plans_kill_switch')
			? expVal('supercharge_timelines_with_plans_experiment', 'cohort', 'not-enrolled')
			: undefined;
	}, []);

	useEffect(() => {
		if (stwpCohort) {
			Promise.all([getTrialSprintBoardProjectId(cloudId), getNumProjects(cloudId)]).then(
				([trialSprintBoardProjectId, numProjects]) => {
					if (typeof numProjects === 'number') {
						const numProjectsExcludingTrial = trialSprintBoardProjectId
							? numProjects - 1
							: numProjects;
						fireOperationalAnalytics(createAnalyticsEvent({}), 'plansPremiumFeatureGate rendered', {
							stwpCohort,
							numProjectsExcludingTrial,
						});
					}
				},
			);
		}
	}, [stwpCohort, cloudId, createAnalyticsEvent]);

	return (
		<ContextualAnalyticsData
			sourceName="plansPremiumFeatureGate"
			sourceType="Modal"
			attributes={{ stwpCohort }}
		>
			<MenuContainer>
				<MenuHeaderContainer>
					<FeatureGateLogo alt="" colorMode={colorMode} />
				</MenuHeaderContainer>
				<ContentContainer>
					<Heading size="small" as="h2">
						{formatMessage(messages.title)}
					</Heading>
					<Box xcss={contentDescriptionStyles}>{formatMessage(messages.description)}</Box>
				</ContentContainer>
				<FooterContainer>
					<LearnMoreButton
						appearance="link"
						href={LEARN_MORE_LINK}
						target="_blank"
						onClick={onLearnMoreClick}
					>
						{formatMessage(messages.learnMoreLink)}
					</LearnMoreButton>
					{expVal(
						'plans_menu_try_it_free_one_stop_shop_modal',
						'isOneStopShopModalEnabled',
						true,
					) ? (
						<TryPremiumButton
							onClick={onStartTrialClick}
							iconBefore={
								<PremiumIcon
									label={formatMessage(messages.imageAlt)}
									color={token('color.text.inverse')}
								/>
							}
						>
							{formatMessage(messages.startTrialButton)}
						</TryPremiumButton>
					) : (
						<TryPremiumButtonDirectLink
							href={premiumTrialUpgradeLink}
							iconBefore={
								<PremiumIcon
									label={formatMessage(messages.imageAlt)}
									color={token('color.text.inverse')}
								/>
							}
						>
							{formatMessage(messages.startTrialButton)}
						</TryPremiumButtonDirectLink>
					)}
				</FooterContainer>
			</MenuContainer>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export default PremiumFeatureGate;

const TryPremiumButton = (props: CustomThemeButtonProps) => (
	<PremiumUpgradeModalEntryPointButton
		{...props}
		theme={(current, themeProps) => ({
			buttonStyles: {
				...current(themeProps).buttonStyles,
				display: 'flex',
				alignItems: 'center',
				background: `linear-gradient(135deg, ${token('color.background.discovery.bold')}, ${token('color.background.brand.bold')})`,
				color: token('color.text.inverse'),
				outline: 'none',
				boxSizing: 'border-box',
				padding: `${token('space.050')} ${token('space.150')}`,
				borderRadius: token('radius.small'),
				height: token('space.400'),
				border: 'none',
				font: token('font.heading.xsmall'),
				gap: token('space.025'),
			},
			spinnerStyles: current(themeProps).spinnerStyles,
		})}
	/>
);

const TryPremiumButtonDirectLink = (props: CustomThemeButtonProps) => (
	<CustomThemeButton
		{...props}
		theme={(current, themeProps) => ({
			buttonStyles: {
				...current(themeProps).buttonStyles,
				display: 'flex',
				alignItems: 'center',
				background: `linear-gradient(135deg, ${token('color.background.discovery.bold')}, ${token('color.background.brand.bold')})`,
				color: token('color.text.inverse'),
				outline: 'none',
				boxSizing: 'border-box',
				padding: `${token('space.050')} ${token('space.150')}`,
				borderRadius: token('radius.small'),
				height: token('space.400'),
				border: 'none',
				font: token('font.heading.xsmall'),
				gap: token('space.025'),
			},
			spinnerStyles: current(themeProps).spinnerStyles,
		})}
	/>
);

const LearnMoreButton = (props: CustomThemeButtonProps) => (
	<CustomThemeButton
		{...props}
		theme={(current, themeProps) => ({
			buttonStyles: {
				...current(themeProps).buttonStyles,
				maxWidth: '250px',
				marginRight: token('space.200'),
			},
			spinnerStyles: current(themeProps).spinnerStyles,
		})}
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuContainer = styled.div({
	boxSizing: 'border-box',
	borderRadius: token('radius.small'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	background: `linear-gradient(135deg, ${token('color.background.accent.blue.subtle')}, ${token('color.background.accent.purple.subtle')})`,
	backgroundOrigin: 'border-box',
	backgroundClip: 'border-box',
	maxWidth: '397px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuHeaderContainer = styled.div({
	background: `linear-gradient(79deg,
        ${token('color.background.accent.blue.subtlest')},
        ${token('color.background.accent.purple.subtlest')})`,
	height: '90px',
	borderRadius: `${token('radius.small')} ${token('radius.small')} 0 0`,
	padding: 0,
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	background: `${token('elevation.surface')}`,
});

const contentDescriptionStyles = xcss({
	marginTop: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	paddingTop: 0,
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	background: `${token('elevation.surface')}`,
	borderRadius: `0 0 ${token('radius.small')} ${token('radius.small')}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});
